/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { ButtonGroup, Dialog, InlineNotification, Typography2 } from '@coursera/cds-core';

import GroupSelectList from 'bundles/enterprise-admin-learners/components/GroupSelectList';
import { useManageableGroups } from 'bundles/enterprise-admin-learners/hooks/useManageableGroups';

import _t from 'i18n!nls/enterprise-admin-learners';

const { HeadingGroup, Content, Actions } = Dialog;

const styles = {
  content: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
};

export type GroupAssignmentStepProps = {
  open: boolean;
  onBack: () => void;
  onSubmit: (groupId: string) => void | Promise<void>;
  /** Caller's mutation/request loading state. */
  submitting: boolean;
  /** Caller's submission error, if any. */
  errorMessage?: string;
};

/**
 * Group picker shown between row/CSV entry and submission. Shared by the email and CSV
 * invite flows, which pass their own (different) submission logic in via `onSubmit`.
 */
const GroupAssignmentStep = ({ open, onBack, onSubmit, submitting, errorMessage }: GroupAssignmentStepProps) => {
  const manageableGroups = useManageableGroups();
  const [selectedGroupId, setSelectedGroupId] = React.useState<string | undefined>(undefined);
  const [selectionError, setSelectionError] = React.useState('');

  const handleSelect = (groupId: string) => {
    setSelectedGroupId(groupId);
    if (selectionError) setSelectionError('');
  };

  const handleSubmit = async () => {
    if (!selectedGroupId) {
      setSelectionError(_t('Choosing a group is mandatory to invite learners.'));
      return;
    }
    try {
      await onSubmit(selectedGroupId);
    } catch {
      // Callers surface their own submission errors via `errorMessage` — this only guards
      // against an uncaught rejection reaching the console.
    }
  };

  return (
    <Dialog open={open} onClose={onBack} width="medium">
      <HeadingGroup supportText={_t('Choose the group to add your learners to.')}>
        {_t('Add learners to a group')}
      </HeadingGroup>
      <Content css={styles.content}>
        <GroupSelectList
          groups={manageableGroups}
          selectedId={selectedGroupId}
          onSelect={handleSelect}
          ariaLabel={_t('Select a group')}
        />
        {/* Always-present live region so screen readers announce errors when they appear. */}
        <div role="alert">
          {(selectionError || errorMessage) && (
            <InlineNotification severity="error">
              <Typography2 component="p">{selectionError || errorMessage}</Typography2>
            </InlineNotification>
          )}
        </div>
      </Content>
      <Actions>
        <ButtonGroup
          primaryButtonProps={{
            children: _t('Add to group'),
            loading: submitting,
            disabled: submitting,
            onClick: handleSubmit,
          }}
          alternateButtonProps={{
            variant: 'secondary',
            children: _t('Back'),
            disabled: submitting,
            onClick: onBack,
          }}
        />
      </Actions>
    </Dialog>
  );
};

export default GroupAssignmentStep;
